import React, { useContext, useState, useEffect } from "react";
import style from "./EditBlock.module.scss";
import { Button, Col, Icon, Input, InputNumber, message, Row } from "antd";
import {
    errorMessage,
    getQuestionTypeName,
    QuestionType
} from "../../../../../service/service";
import PaperContext from "../../context";
import { checkTopic, TopicType } from "../../../../../service/homework";
import usePromiseCallback from "../../../../../utils/usePromiseCallback";
import { saveExamTopic } from "../../../../../service/exam";
import Editor from "components/Editor";
import useRequestFn from "utils/useRequestFn";

export interface EditBlockOption {
    hasDelete?: boolean;
    hasAdd?: boolean;
    hasSave?: boolean;
    hasScore?: boolean;
    customCheckTopic?: (topic: TopicType) => {
        result: "success" | "error";
        msg?: string;
    };
    onDelete?: () => void;
}

interface EditBlockProps {
    type: QuestionType;
    children: React.ReactNode;
    topic: TopicType;
    hasDelete?: EditBlockOption["hasDelete"];
    hasAdd?: EditBlockOption["hasAdd"];
    hasSave?: EditBlockOption["hasSave"];
    hasScore?: EditBlockOption["hasScore"];
    autoScore?: number;
    disableScore?: boolean;
    customCheckTopic?: EditBlockOption["customCheckTopic"];
    onDelete?: EditBlockOption["onDelete"];
    onChange: (topic: TopicType) => void;
}

export function EditBlock({
    type,
    children,
    topic,
    hasDelete = false,
    hasAdd = true,
    hasSave = true,
    hasScore = true,
    autoScore,
    disableScore = false,
    customCheckTopic,
    onDelete,
    onChange
}: EditBlockProps) {
    const [trunk, setTrunk] = useState(topic.trunk);
    const [score, setScore] = useState(topic.score || 0);
    const {
        paperId,
        addTopic,
        lockEdit,
        loadPaperData,
        loadExamPaperData,
        examId,
        setChecked,
        examPaperData
    } = useContext(PaperContext);
    const [inputTrunk, setInputTrunk] = useState(true);
    const [saveExam, { loading }] = useRequestFn(saveExamTopic);

    useEffect(() => {
        setInputTrunk(!/<[^>]+>/g.test(trunk));
    }, [trunk]);

    useEffect(() => {
        autoScore && setScore(autoScore);
    }, [autoScore]);

    const onSave = usePromiseCallback(async () => {
        const scoreInt = score || 0; // 有可能是NaN

        const { result, msg } = customCheckTopic
            ? customCheckTopic({ ...topic, trunk, score: scoreInt })
            : checkTopic({ ...topic, trunk, score: scoreInt });

        if (result === "error") {
            return message.error(msg).promise;
        }

        try {
            await saveExam(
                topic.topicId,
                trunk,
                type,
                { ...topic, score: scoreInt },
                examId,
                paperId
            );
            if (!paperId) {
                if (examPaperData.length !== 0) {
                    setChecked(examPaperData.length);
                } else {
                    setChecked(0);
                }
            }
            await (loadExamPaperData && loadExamPaperData());
            await loadPaperData();
            message.info("保存成功!");
        } catch (e) {
            message.error(errorMessage(e));
        }
    });

    const onScoreChange = (score: number) => {
        onChange({ ...topic, score });
        setScore(score);
    };

    return (
        <EditBlockPure
            inputTrunk={inputTrunk}
            trunk={trunk}
            onChange={onChange}
            type={type}
            topic={topic}
            setTrunk={setTrunk}
            children={children}
            hasSave={hasSave}
            hasDelete={hasDelete}
            onDelete={onDelete}
            setInputTrunk={setInputTrunk}
            disableScore={disableScore}
            lockEdit={lockEdit}
            addTopic={addTopic}
            onSave={onSave}
            score={score}
            onScoreChange={onScoreChange}
            hasAdd={hasAdd}
            loading={loading}
        />
    );
}

interface EditBlockPureProps {
    inputTrunk: boolean;
    trunk: string;
    onChange: (topic: TopicType) => void;
    type: QuestionType;
    topic: TopicType;
    setTrunk: (val: string) => void;
    children: React.ReactNode;
    hasSave: EditBlockOption["hasSave"];
    hasAdd: EditBlockOption["hasAdd"];
    hasDelete: EditBlockOption["hasDelete"];
    onDelete: EditBlockOption["onDelete"];
    setInputTrunk: (val: boolean) => void;
    disableScore: boolean;
    lockEdit: boolean;
    addTopic: (type: QuestionType) => void;
    onSave: () => void;
    score: number;
    onScoreChange: (score: number) => void;
    loading?: boolean;
}

export function EditBlockPure({
    inputTrunk,
    trunk,
    children,
    hasAdd,
    hasDelete,
    hasSave,
    onChange,
    onDelete,
    setTrunk,
    topic,
    type,
    setInputTrunk,
    addTopic,
    disableScore,
    lockEdit,
    onSave,
    score,
    onScoreChange,
    loading
}: EditBlockPureProps) {
    return (
        <div className={style.root}>
            <h1 className={`${style.type} ${style.label}`}>
                {getQuestionTypeName(type)}
            </h1>

            <Row
                type="flex"
                align="middle"
                className={`${style.padding} ${style.content}`}>
                <Col span={2} className={style.label}>
                    题目：
                </Col>

                <Col span={20}>
                    {inputTrunk && (
                        <Input
                            placeholder="填写题干"
                            size="large"
                            onBlur={() => onChange({ ...topic, trunk })}
                            value={trunk}
                            onChange={e => setTrunk(e.target.value)}
                        />
                    )}
                    {!inputTrunk && (
                        <Editor
                            data={trunk}
                            onChange={text => setTrunk(text)}
                            onBlur={() => onChange({ ...topic, trunk })}
                        />
                    )}
                </Col>
                <Col offset={1} span={1}>
                    <Button
                        type="primary"
                        icon={inputTrunk ? "form" : "edit"}
                        onClick={() => setInputTrunk(!inputTrunk)}
                    />
                </Col>
            </Row>

            <Row className={style.padding}>{children}</Row>

            <Row className={`${style.padding} ${style.score}`}>
                <Col span={2} className={style.label}>
                    分值：
                </Col>
                <Col span={3}>
                    <InputNumber
                        min={0}
                        max={100}
                        placeholder="分数"
                        disabled={disableScore}
                        value={score}
                        onChange={value => onScoreChange(value || 0)}
                        precision={1}
                    />
                </Col>
            </Row>

            <Row className={style.buttons}>
                {hasSave && (
                    <Button
                        type="primary"
                        size={"large"}
                        onClick={onSave}
                        disabled={lockEdit}
                        loading={loading}>
                        <Icon type="cloud-upload" />{" "}
                        {topic.topicId ? "修改" : "保存"}
                    </Button>
                )}
                {hasAdd && (
                    <Button
                        type="primary"
                        ghost
                        size={"large"}
                        onClick={() => addTopic(type)}
                        disabled={lockEdit}>
                        <Icon type="plus" /> 继续添加
                    </Button>
                )}
                {hasDelete && (
                    <Button
                        type="danger"
                        ghost
                        size={"large"}
                        onClick={onDelete}
                        disabled={lockEdit}>
                        <Icon type="delete" /> 删除
                    </Button>
                )}
            </Row>
        </div>
    );
}
